import { HomePage } from "./pages/home.js";
import { CreatePage } from "./pages/create.js";
import { LibraryPage } from "./pages/library.js";
import { LoginPage, SignupPage, ResetPasswordPage, UpgradePage } from "./pages/auth.js";
import { MemoryPage } from "./pages/memory.js";
import { CampaignsPage } from "./pages/campaigns.js";
import { HelpPage } from "./pages/stubs.js";
import { AnalyticsPage } from "./pages/analytics.js";
import { CalendarPage } from "./pages/calendar.js";
import { SettingsPage } from "./pages/settings.js";
import { ConnectorsPage } from "./pages/connectors.js";
import { AgentInboxPage } from "./pages/agentInbox.js";
import { AutopilotPage } from "./pages/autopilot.js";
import { AdminPage } from "./pages/admin.js";
import { AcceptInvitePage } from "./pages/acceptInvite.js";
import { ClientViewPage } from "./pages/clientView.js";
import { McpFeaturesPage } from "./pages/mcpFeatures.js";
import { trackPageView } from "./utils/analytics.js";

const registry = {
  home: HomePage,
  create: CreatePage,
  library: LibraryPage,
  login: LoginPage,
  signup: SignupPage,
  calendar: CalendarPage,
  analytics: AnalyticsPage,
  help: HelpPage,
  memory: MemoryPage,
  campaigns: CampaignsPage,
  settings: SettingsPage,
  connectors: ConnectorsPage,
  'agent-inbox': AgentInboxPage,
  autopilot: AutopilotPage,
  admin: AdminPage,
  reset: ResetPasswordPage,
  upgrade: UpgradePage,
  'accept-invite': AcceptInvitePage,
  'client-view': ClientViewPage,
  'mcp-features': McpFeaturesPage,
};

// Phase 7 follow-up — mcp-features is the public marketing/landing page
// for the MCP connector. Linked from the auth hero. Stays public so
// trial-curious visitors can read it without an account.
const PUBLIC_ROUTES = ["login", "signup", "help", "reset", "upgrade", "accept-invite", "client-view", "mcp-features"];
let authInProgress = false;

export function setAuthInProgress(val) {
  authInProgress = val;
}

export function createRouter() {
  console.log("[ROUTER] Registry Keys:", Object.keys(registry));
  let navigationLock = Promise.resolve();
  let current = null;

  function getRoute() {
    const raw = (location.hash || "#home").replace("#", "").split("?")[0].trim();
    return registry[raw] ? raw : "home";
  }

  async function go(route) {
    // Wait for previous navigation to finish
    await navigationLock;

    let resolveNav;
    navigationLock = new Promise(res => resolveNav = res);

    try {
      const isAuthed = !!window.__auth__;
      let name = registry[route] ? route : "home";
      console.log(`[ROUTER] go route=${route} name=${name} isAuthed=${isAuthed} lock=${authInProgress}`);

      if (authInProgress) {
        console.log("[ROUTER] Auth in progress, skipping guard/mount");
        return;
      }

      // Route Guards
      if (!isAuthed) {
        if (!PUBLIC_ROUTES.includes(name)) {
          console.warn("[ROUTER] Unauthed access to", name, "-> redirecting #login");
          location.hash = "#login";
          return;
        }
      } else {
        if (name === "login" || name === "signup") {
          console.log("[ROUTER] Authed user on auth page -> redirecting #home");
          location.hash = "#home";
          return;
        }
      }

      // G7: Tier lockout — expired trial + no Stripe + not admin → force upgrade
      if (isAuthed && !['upgrade', 'login', 'signup', 'help', 'reset', 'accept-invite', 'client-view', 'mcp-features'].includes(name)) {
          const u = window.__auth__;
          if (u && u.trial_expires_at && !u.has_stripe) {
              if (!['admin', 'angel_admin', 'god_admin'].includes(u.role)) {
                  const expired = new Date(u.trial_expires_at) < new Date();
                  if (expired) {
                      console.warn('[ROUTER] Trial expired — redirecting to #upgrade');
                      location.hash = '#upgrade';
                      return;
                  }
              }
          }
      }

      // Phase 2 (UX audit): when the user navigates to the Help center,
      // remember which page they came from so HelpPage.mount() can default
      // the sidebar group to the matching surface (calendar entry → Calendar
      // group, library entry → Library group, etc.). Cleared on signout via
      // the same sessionStorage scope.
      if (name === 'help' && current?._prevName) {
        try { sessionStorage.setItem('help.entryRoute', current._prevName); } catch (_) {}
      }

      // Mount Logic
      document.body.dataset.route = name;
      document.querySelectorAll(".page").forEach((p) => p.classList.remove("active"));
      const shell = document.getElementById("app-root");
      if (shell) shell.dataset.page = name;

      const pageEl = document.getElementById("page-" + name);
      if (!pageEl) throw new Error("UI_CONTRACT_VIOLATION: missing #page-" + name);
      pageEl.classList.add("active");

      // Phase 34-C: top-bar breadcrumb removed — page title in the
      // page-header is the unique identifier; sidebar nav active state
      // shows current location. No #current-page-title element to write.

      // Tear down the previous page so its DOM, listeners, and timers
       // don't leak. Pages can hook unmount() to detach intervals/observers;
       // the router then empties the section so stale content doesn't sit
       // around in the DOM (querySelector would otherwise see duplicates).
      const prevSection = current?.el || (current?._prevName ? document.getElementById('page-' + current._prevName) : null);
      if (current?.unmount) {
        try { await current.unmount(); } catch (e) { console.warn('[ROUTER] unmount threw:', e); }
      }
      if (prevSection && prevSection !== pageEl) prevSection.innerHTML = '';

      const Page = registry[name];
      current = new Page();
      current._prevName = name; // remember which section we mounted into
      await current.mount(pageEl);

      // Expose the current page instance for debugging / testing. Used by
      // Playwright scripts to reach into state like `blogBrandScore` and
      // `activeBlogPost` without having to mock the full UI flow.
      window.__currentPage__ = current;

      // Session B1 — GA4 sees only the initial page load on a hash-routed
      // SPA; emit a virtual page_view per successful route mount.
      trackPageView(name);

      console.log("[ROUTER] route=" + name);
    } finally {
      resolveNav();
    }
  }

  function start() {
    window.addEventListener("hashchange", () => go(getRoute()));
    go(getRoute());
  }

  return { start, go };
}
